import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { z } from 'zod'
import {
  CompanyDot,
  customerProjectsQueryOptions,
  directoryQueryOptions,
  findCompany,
  findProject,
  projectColor,
} from '@/entities/directory'
import { isCustomerSession, useSession } from '@/entities/session'
import { EntityTagsEditor, entityTagsQueryOptions } from '@/entities/tag'
import { fromISODate, toISODate } from '@/shared/lib'
import { Spinner } from '@/shared/ui/spinner'
import { CalendarBoard } from '@/widgets/calendar-board'

const searchSchema = z.object({
  view: z.enum(['day', 'week', 'month']).optional().catch(undefined),
  date: z.string().optional().catch(undefined),
})

export const Route = createFileRoute('/_app/projects/$projectId')({
  validateSearch: (search: Record<string, unknown>) => searchSchema.parse(search),
  component: ProjectCalendarPage,
})

// Проектная линза — одна на оба контура (00-architecture § 6). Сотрудник видит
// путь «компания / проект» с тегами и заводит события; заказчик — витрину своего
// проекта: те же сетка и период, но без создания, правки и студийных ссылок.
function ProjectCalendarPage() {
  const { projectId } = Route.useParams()
  const session = useSession()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const view = search.view ?? 'month'
  const anchor = (search.date ? fromISODate(search.date) : null) ?? new Date()
  const customer = isCustomerSession(session)

  // Источник подписи — по контуру: справочник студии заказчику закрыт (403), его
  // проекты приходят своей выдачей.
  const { data: companies = [] } = useQuery({ ...directoryQueryOptions(), enabled: !customer })
  const { data: customerProjects = [] } = useQuery({
    ...customerProjectsQueryOptions(),
    enabled: customer,
  })
  const project = findProject(companies, projectId)
  const company = project ? findCompany(companies, project.companyId) : undefined
  const customerProject = customerProjects.find((p) => p.id === projectId)

  return (
    <div className="flex h-full min-h-0 flex-col">
      {customer
        ? customerProject && (
            <div className="flex flex-wrap items-center gap-2 border-b px-gutter py-2">
              <span className="label">Проект</span>
              <span className="flex items-center gap-1.5 text-sm">
                <CompanyDot color={customerProject.color} />
                {customerProject.name}
              </span>
              <span className="text-sm text-text-secondary">
                · события проекта, открытые вашей компании
              </span>
            </div>
          )
        : project && (
            <div className="flex flex-wrap items-center gap-2 border-b px-gutter py-2">
              <span className="label">Линза · проект</span>
              <span className="flex items-center gap-1.5 text-sm">
                {company && (
                  <Link
                    to="/companies/$companyId"
                    params={{ companyId: company.id }}
                    className="flex items-center gap-1.5 text-text-secondary transition-colors hover:text-text-primary"
                  >
                    <CompanyDot color={company.color} />
                    {company.name}
                  </Link>
                )}
                <span className="text-text-tertiary">/</span>
                <span className="flex items-center gap-1.5">
                  <CompanyDot color={projectColor(companies, project)} />
                  {project.name}
                </span>
              </span>
              <div className="ml-auto">
                <ProjectTags projectId={projectId} />
              </div>
            </div>
          )}
      <CalendarBoard
        lens={{ kind: 'project', id: projectId }}
        view={view}
        anchor={anchor}
        readOnly={customer}
        onViewChange={(v) => void navigate({ search: (s) => ({ ...s, view: v }) })}
        onAnchorChange={(d) => void navigate({ search: (s) => ({ ...s, date: toISODate(d) }) })}
        {...(customer
          ? {}
          : {
              createDefaults: { projectId, ...(project ? { companyId: project.companyId } : {}) },
            })}
      />
    </div>
  )
}

// Теги проекта — тот же хабовый набор, что видят task/deal на этом проекте
// (00-architecture § 5): отдельная ручка /api/tags/project/{id}, правка здесь.
function ProjectTags({ projectId }: { projectId: string }) {
  const tags = useQuery(entityTagsQueryOptions('project', projectId))
  if (tags.isPending) return <Spinner label="Загрузка тегов" />
  return <EntityTagsEditor entityKind="project" entityId={projectId} tags={tags.data ?? []} />
}
